import axios from 'axios';
import { WizardData } from '../context/WizardContext';

interface ProgressData {
  sessionId: string;
  customerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  currentStep: number;
  stepName: string;
  formData: WizardData;
  lastUpdated: string;
  isComplete: boolean;
  createdAt: string;
}

class MongoApiService {
  private apiUrl: string | null = null;

  constructor() {
    // Get the base API URL from environment
    this.apiUrl = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8080';
  }

  /**
   * Create axios config with authorization header
   */
  private getAxiosConfig(token?: string) {
    if (!token) {
      return {};
    }
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  }

  async saveProgress(progressData: ProgressData, token?: string): Promise<void> {
    if (!this.apiUrl) {
      throw new Error('API URL not configured');
    }

    try {
      await axios.post(`${this.apiUrl}/api/progress/save`, progressData, this.getAxiosConfig(token));
    } catch (error) {
      console.error('Failed to save to MongoDB API:', error);
      throw error;
    }
  }

  async getIncompleteSessions(token?: string): Promise<ProgressData[]> {
    if (!this.apiUrl) {
      return [];
    }

    try {
      const response = await axios.get(`${this.apiUrl}/api/progress/incomplete`, this.getAxiosConfig(token));
      return response.data;
    } catch (error) {
      console.error('Failed to fetch from MongoDB API:', error);
      return [];
    }
  }

  async getCompletedSessions(token?: string): Promise<ProgressData[]> {
    if (!this.apiUrl) {
      return [];
    }

    try {
      const response = await axios.get(`${this.apiUrl}/api/progress/completed`, this.getAxiosConfig(token));
      return response.data;
    } catch (error) {
      console.error('Failed to fetch completed sessions from MongoDB API:', error);
      return [];
    }
  }

  async getProgress(sessionId: string, token?: string): Promise<ProgressData | null> {
    if (!this.apiUrl) {
      return null;
    }

    try {
      const response = await axios.get(`${this.apiUrl}/api/progress/${sessionId}`, this.getAxiosConfig(token));
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null; // Session not found
      }
      console.error('Failed to fetch progress from MongoDB API:', error);
      throw error;
    }
  }

  async deleteSession(sessionId: string, token?: string): Promise<void> {
    if (!this.apiUrl) {
      throw new Error('API URL not configured');
    }

    try {
      await axios.delete(`${this.apiUrl}/api/progress/${sessionId}`, this.getAxiosConfig(token));
    } catch (error) {
      console.error('Failed to delete from MongoDB API:', error);
      throw error;
    }
  }
}

export const mongoApiService = new MongoApiService();